
import RandomIdGenerator from '@/utils/RandomIdGenerator';
import { useScheduleStore } from '@/stores/schedule';
import { useResourceStore } from '@/stores/resource';

const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_DAYS_PER_WEEK = 5;
const DEFAULT_DAYS_PER_MONTH = 20;

// Seed calendar created in every project DB (mirrors the API's
// TimeRangeDurationHelper.DefaultCalendarId). The API rejects a commit when a
// tracking (non-'none') time range has no resolvable calendarId, because it
// normalizes duration.weighting against that calendar, so fall back to the
// seed calendar when neither the schedule mapping nor the task provides one.
export const DEFAULT_CALENDAR_ID = 'MMCAL247';

export class ExcelProcessorUtils {
    constructor(selectedSchedule, selectedTimeRangeTemplate) {
      this.scheduleStore = useScheduleStore();
      this.resourceStore = useResourceStore();
      this.idGenerator = new RandomIdGenerator();
      this.selectedSchedule = selectedSchedule;
      this.selectedTimeRangeTemplate = selectedTimeRangeTemplate;
    }

    generateNewId() {
      return this.idGenerator.generate();
    }

    buildTaskHierarchy(tasks) {
      const taskMap = new Map();
      const rootTasks = [];

      tasks.forEach(task => {
        taskMap.set(task.id, { ...task, children: [], childTaskIds: [] });
      });

      tasks.forEach(task => {
        const taskWithChildren = taskMap.get(task.id);
        if (task.parentTaskId) {
          const parentTask = taskMap.get(task.parentTaskId);
          if (parentTask) {
            parentTask.children.push(taskWithChildren);
            parentTask.childTaskIds.push(taskWithChildren.id);
          }
        } else {
          rootTasks.push(taskWithChildren);
        }
      });

      return rootTasks;
    }

    createTaskMap(tasks) {
      const taskMap = {};
      
      function addTaskToMap(task) {
        taskMap[task.id] = task;
        if (task.children && task.children.length > 0) {
          task.children.forEach(childTask => addTaskToMap(childTask));
        }
      }
      
      tasks.forEach(task => addTaskToMap(task));
      return taskMap;
    }

    // Shape of the API's SubTaskDTO — no createdBy* / parentTaskId, which the
    // Subtask model doesn't have (the parent is the task carrying the subtask).
    createSubtask({ title, type, code, id }) {
        return {
          id: id || this.generateNewId(),
          title,
          leadByTeamId: "",
          weighting: 1,
          itemId: "",
          itemType: type,
          itemTemplateCode: code || "",
          progress: 0,
          meta: {
            versionReference: this.generateNewId()
          },
          scheduleId: this.selectedSchedule.id
        };
    }
  
    calculateDatesFromChildren(task) {
    if (!task || !task.children || task.children.length === 0) {
      return { startDate: null, duration: null };
    }

    const childTasks = task.children;

    if (childTasks.length === 0) {
      return { startDate: null, duration: null };
    }

    let earliestStartDate = null;

    childTasks.forEach(childTask => {
      if (!childTask.timeRanges || childTask.timeRanges.length === 0) {
        return;
      }

      childTask.timeRanges.forEach(timeRange => {
        if (timeRange.startDate) {
          const childStartDate = new Date(timeRange.startDate);
          if (!earliestStartDate || childStartDate < earliestStartDate) {
            earliestStartDate = childStartDate;
          }
        }
      });
    });

    if (!earliestStartDate) {
      return { startDate: null, duration: null };
    }

    return {
      startDate: earliestStartDate,
      duration: null
    };
  }

  getTimeRanges(existingTask, startDate, duration) {
    startDate = this.parseDate(startDate);
    const parsed = this.parseDurationShorthand(duration);

    if (existingTask) {
      // An existing task's stored calendar is authoritative on update — the
      // schedule's timeRangeCalendars mapping only fills in when the task has
      // none (and seeds new tasks below). A Calendar Override cell, when the
      // column is present, still overwrites afterwards (ExcelProcessor.createTask).
      return existingTask.timeRanges.map(timeRange => {
        const calendarMapping = this.getCalendarForTimeRange(this.selectedSchedule, timeRange.timeRangeTemplateId);
        const calendarId = this.resolveTimeRangeCalendarId(
          timeRange.calendarId || calendarMapping.calendarId,
          timeRange.timeRangeTemplateType
        );
        // Keep the versionReference paired with whichever calendar won above.
        const calendarVersionReference = timeRange.calendarId
          ? timeRange.calendarVersionReference || null
          : calendarMapping.calendarVersionReference || null;
        const dur = this.migrateDuration(timeRange, calendarId);
        const { durationInfo: _dropped, ...rest } = timeRange; // eslint-disable-line no-unused-vars
        return {
          ...rest,
          startDate: timeRange.startDate,
          duration: dur,
          calendarId,
          calendarVersionReference,
          progress: timeRange.progress,
          timeRangeTemplateId: timeRange.timeRangeTemplateId,
          timeRangeTemplateType: timeRange.timeRangeTemplateType
        };
      });
    }

    const scheduleTemplatesObj = this.scheduleStore.getScheduleTemplatesObj;
    const scheduleTemplate = scheduleTemplatesObj[this.selectedSchedule.scheduleTemplateId]?.[0];

    if (!scheduleTemplate) {
      return [];
    }

    return scheduleTemplate.timeRangeTemplates.map(template => {
      const calendarMapping = this.getCalendarForTimeRange(this.selectedSchedule, template.id);
      const calendarId = this.resolveTimeRangeCalendarId(calendarMapping.calendarId, template.type);
      const dur = parsed || { value: 1, unit: 'day' };
      return {
        timeRangeTemplateId: template.id,
        timeRangeTemplateType: template.type,
        startDate: startDate ? startDate.toISOString() : null,
        duration: this.ensureWeighting(dur, calendarId),
        calendarId,
        calendarVersionReference: calendarMapping.calendarVersionReference || null,
        progress: 0
      };
    });
  }

  getTimeRangeTemplateIds() {
    const scheduleTemplatesObj = this.scheduleStore.getScheduleTemplatesObj;
    const scheduleTemplate = scheduleTemplatesObj[this.selectedSchedule?.scheduleTemplateId]?.[0];
    return (scheduleTemplate?.timeRangeTemplates || []).map(template => template.id);
  }
  
  /**
   * Process parent task dates after all tasks have been processed and the hierarchy is built.
   * This calculates startDate, endDate, and duration for parent tasks based on their child tasks
   * when those values are blank.
   * @param {Array} tasks - The hierarchical tasks with parent-child relationships established
   * @returns {Array} - The processed tasks with updated dates
   */
  processParentTaskDates(tasks) {
    // Process tasks recursively, bottom-up

    const processTask = (task) => {

      // Process children first (bottom-up approach)
      if (task.children && task.children.length > 0) {
        task.children.forEach(child => processTask(child));

        // Calculate dates from children
        const calculatedValues = this.calculateDatesFromChildren(task);

        if (calculatedValues.startDate && this.selectedTimeRangeTemplate) {
          // Update only the selected time range template, not all time ranges
          const selectedTimeRange = task.timeRanges.find(tr => tr.timeRangeTemplateId === this.selectedTimeRangeTemplate.id);

          if (selectedTimeRange) {
            if (!selectedTimeRange.startDate && calculatedValues.startDate) {
              selectedTimeRange.startDate = calculatedValues.startDate.toISOString();
            }

            if (calculatedValues.duration) {
              selectedTimeRange.duration = calculatedValues.duration;
            }
          }
        }
      }

      return task;
    };

    // Process all root tasks
    return tasks.map(task => processTask(task));
  }

      isTimeRangeUnchanged(existingTask, newStartDate, newDuration) {
        if (!existingTask || !existingTask.timeRanges || !this.selectedTimeRangeTemplate) {
          return false;
        }
        const existingSelected = existingTask.timeRanges.find(
          tr => tr.timeRangeTemplateId === this.selectedTimeRangeTemplate.id
        );
        if (!existingSelected) return false;

        const existingStartMs = existingSelected.startDate ? new Date(existingSelected.startDate).getTime() : null;
        const newStartMs = newStartDate ? newStartDate.getTime() : null;
        if (existingStartMs !== newStartMs) return false;

        // An empty duration cell leaves the existing duration untouched, so only
        // a parseable value can differ from the existing duration.
        const parsedNewDuration = this.parseDurationShorthand(newDuration);
        if (!parsedNewDuration) return true;

        const existingDuration = this.migrateDuration(existingSelected, existingSelected.calendarId ?? null);
        return existingDuration.value === parsedNewDuration.value &&
               existingDuration.unit === parsedNewDuration.unit;
      }

      processTimeRanges(timeRanges, startDate, duration) {
        if (!this.selectedTimeRangeTemplate) return;
        const selectedTimeRange = timeRanges.find(tr => tr.timeRangeTemplateId === this.selectedTimeRangeTemplate.id);
        if (!selectedTimeRange) {
          throw new Error('Selected timeRange not found');
        }
        const pad = (num) => num.toString().padStart(2, '0');
        const date = new Date(startDate.getTime());
        selectedTimeRange.startDate = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}Z`;
        // Parse before truthiness-testing: a numeric 0 cell is falsy but is a
        // valid zero duration.
        const parsed = this.parseDurationShorthand(duration);
        if (parsed) {
          selectedTimeRange.duration = this.ensureWeighting(parsed, selectedTimeRange.calendarId);
        }
      }
  
      parseDate(value) {
        if (!value) return null;
        
        if (value instanceof Date) return value;
        
        if (typeof value === 'string') {
          const [datePart, timePart] = value.split(' ');
          const [year, month, day] = datePart.split('-');
          const [hours, minutes, seconds] = timePart ? timePart.split(':') : [0, 0, 0];
          
          return new Date(Date.UTC(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds || 0) 
          ));
        }
      
        console.warn(`Invalid date value: ${value}`);
        return null;
      }

      parseDurationShorthand(value) {
        if (value === null || value === undefined || value === '') return null;

        const strValue = String(value).trim();

        // Legacy d.HH:MM:SS format (e.g. "5.00:00:00", "1.08:30:00")
        const legacyPattern = /^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/;
        const legacyMatch = strValue.match(legacyPattern);
        if (legacyMatch) {
          return this.migrateLegacyTimeSpan(strValue);
        }

        // Shorthand format: number + optional unit suffix
        const shorthandPattern = /^(\d+\.?\d*)\s*(mo|m|h|d|w|y)?$/i;
        const match = strValue.match(shorthandPattern);
        if (match) {
          const num = parseFloat(match[1]);
          const suffix = (match[2] || 'd').toLowerCase();
          const unitMap = {
            'm': 'minute',
            'h': 'hour',
            'd': 'day',
            'w': 'week',
            'mo': 'month',
            'y': 'year'
          };
          const unit = unitMap[suffix] || 'day';
          // No weighting yet — it depends on the time range's calendar, so it is
          // filled in by ensureWeighting() once the calendarId is known. The API
          // recomputes weighting from value + unit + calendar on commit anyway.
          return { value: num, unit };
        }

        console.warn(`Invalid duration value: ${value}`);
        return null;
      }

      formatDurationShorthand(duration) {
        if (!duration || duration.value === null || duration.value === undefined) return '';
        const unitMap = {
          'minute': 'm',
          'hour': 'h',
          'day': 'd',
          'week': 'w',
          'month': 'mo',
          'year': 'y'
        };
        const suffix = unitMap[duration.unit] || 'd';
        return `${duration.value}${suffix}`;
      }

      migrateLegacyTimeSpan(durationString) {
        if (!durationString || typeof durationString !== 'string') return null;
        const legacyPattern = /^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/;
        const match = durationString.match(legacyPattern);
        if (!match) return null;

        const days = parseInt(match[1]);
        const hours = parseInt(match[2]);
        const minutes = parseInt(match[3]);

        if (days === 0 && hours === 0 && minutes > 0) {
          return { value: minutes, unit: 'minute' };
        }
        if (days === 0 && hours > 0) {
          const totalHours = hours + (minutes / 60);
          return { value: totalHours, unit: 'hour' };
        }
        const totalDays = Number((days + (hours / 24) + (minutes / 1440)).toFixed(4));
        return { value: totalDays, unit: 'day' };
      }

      migrateDuration(timeRange, calendarId) {
        if (timeRange.duration && timeRange.duration.value !== undefined) {
          return this.ensureWeighting(timeRange.duration, calendarId);
        }
        if (timeRange.durationInfo) {
          const d = timeRange.durationInfo;
          const value = d.duration ?? d.value;
          const unit = d.durationUnit ?? d.unit ?? 'day';
          return this.ensureWeighting({ value, unit, weighting: d.weighting }, calendarId);
        }
        if (typeof timeRange.duration === 'string') {
          const legacy = this.migrateLegacyTimeSpan(timeRange.duration);
          if (legacy) return this.ensureWeighting(legacy, calendarId);
        }
        return this.ensureWeighting({ value: 1, unit: 'day' }, calendarId);
      }

      computeWeighting(value, unit, calendarObj) {
        const safeValue = value < 0 ? 0 : value;
        const hoursPerDay = calendarObj?.hoursPerDay > 0 ? calendarObj.hoursPerDay : DEFAULT_HOURS_PER_DAY;
        const daysPerWeek = calendarObj?.daysPerWeek > 0 ? calendarObj.daysPerWeek : DEFAULT_DAYS_PER_WEEK;
        const daysPerMonth = calendarObj?.daysPerMonth > 0 ? calendarObj.daysPerMonth : DEFAULT_DAYS_PER_MONTH;

        switch (unit) {
          case 'minute': return safeValue / 60;
          case 'hour':   return safeValue;
          case 'day':    return safeValue * hoursPerDay;
          case 'week':   return safeValue * daysPerWeek * hoursPerDay;
          case 'month':  return safeValue * daysPerMonth * hoursPerDay;
          case 'year':   return safeValue * 12 * daysPerMonth * hoursPerDay;
          default:       return safeValue * hoursPerDay;
        }
      }

      ensureWeighting(dur, calendarId) {
        if (dur.weighting !== undefined && dur.weighting !== null) return dur;
        const calendarObj = this.resolveCalendar(calendarId);
        return { ...dur, weighting: this.computeWeighting(dur.value, dur.unit, calendarObj) };
      }

      resolveCalendar(calendarId) {
        if (!calendarId) return null;
        const calendars = this.scheduleStore.getCalendars || [];
        return calendars.find(c => c.id === calendarId) || null;
      }

      /**
       * The API requires every tracking (non-'none') time range in a commit to
       * carry a calendarId that resolves to a project calendar. 'none' ranges
       * have no scheduling semantics and may omit it.
       */
      resolveTimeRangeCalendarId(calendarId, timeRangeTemplateType) {
        if (calendarId) return calendarId;
        return timeRangeTemplateType === 'none' ? null : DEFAULT_CALENDAR_ID;
      }

      /**
       * Look up the calendar assigned to a time range template from the schedule's timeRangeCalendars.
       */
      getCalendarForTimeRange(schedule, timeRangeTemplateId) {
        if (!schedule || !schedule.timeRangeCalendars || !timeRangeTemplateId) {
          return { calendarId: null, calendarVersionReference: null };
        }
        const mapping = schedule.timeRangeCalendars.find(
          trc => trc.timeRangeTemplateId === timeRangeTemplateId
        );
        if (!mapping) {
          return { calendarId: null, calendarVersionReference: null };
        }
        return {
          calendarId: mapping.calendarId || null,
          calendarVersionReference: mapping.calendarVersionReference || null
        };
      }

processViewableLocations(row, viewableLocationColumns, existingTask) {
        let viewableLocationIds = [];
        
        if (viewableLocationColumns && Array.isArray(viewableLocationColumns) && viewableLocationColumns.length > 0) {
          const locations = this.resourceStore.getLocationsList || [];
          viewableLocationColumns.forEach(colIndex => {
            const cellValue = row[colIndex];
            if (cellValue) {
              const matchingLocation = locations.find(loc =>
                loc.location?.toString().trim().toLowerCase() === cellValue.toString().trim().toLowerCase()
              );
              if (matchingLocation) {
                viewableLocationIds.push(matchingLocation.id.toString());
              }
            }
          });
        } else if (viewableLocationIds.length === 0 && existingTask?.viewableLocationIds) {
          // If no new viewable locations are found, use existing ones (if any)
          viewableLocationIds = existingTask.viewableLocationIds;
        }     
        
        return viewableLocationIds;
      }
  
  }